import { useCallback, useEffect, useRef, useState } from 'react';

// Use the runtime's lib.dom types when available; fall back to a
// minimal local shape so the build doesn't fail on older `@types/dom`.
type Sentinel = {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
};

interface NavigatorWithWakeLock {
  wakeLock?: { request(type: 'screen'): Promise<Sentinel> };
}

/**
 * Screen Wake Lock helper. Once requested, the OS won't dim/lock the
 * screen mid-meeting. The browser auto-releases the lock when the tab
 * loses visibility (e.g. switching apps); a `visibilitychange` listener
 * re-acquires when the tab returns to visible.
 *
 * Browsers that don't expose `navigator.wakeLock` (older Safari, some
 * Firefox builds) silently no-op via `supported = false`.
 */
export interface UseWakeLockResult {
  active: boolean;
  supported: boolean;
  request(): Promise<void>;
  release(): Promise<void>;
}

export function useWakeLock(): UseWakeLockResult {
  const sentinelRef = useRef<Sentinel | null>(null);
  const wantedRef = useRef(false);
  const [active, setActive] = useState(false);

  const supported =
    typeof navigator !== 'undefined' &&
    Boolean((navigator as unknown as NavigatorWithWakeLock).wakeLock);

  const acquire = useCallback(async () => {
    const wl = (navigator as unknown as NavigatorWithWakeLock).wakeLock;
    if (!wl) return;
    try {
      const sentinel = await wl.request('screen');
      sentinelRef.current = sentinel;
      setActive(true);
      sentinel.addEventListener('release', () => {
        sentinelRef.current = null;
        setActive(false);
      });
    } catch {
      // permission denied or page not visible — ignore; the OS is the source of truth.
      setActive(false);
    }
  }, []);

  const request = useCallback(async () => {
    wantedRef.current = true;
    if (sentinelRef.current && !sentinelRef.current.released) return;
    await acquire();
  }, [acquire]);

  const release = useCallback(async () => {
    wantedRef.current = false;
    const sentinel = sentinelRef.current;
    if (sentinel && !sentinel.released) {
      await sentinel.release();
    }
    sentinelRef.current = null;
    setActive(false);
  }, []);

  // Re-acquire on visibility return.
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === 'visible' && wantedRef.current) {
        void acquire();
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [acquire]);

  // Release on unmount so the OS reclaims the lock cleanly.
  useEffect(() => {
    return () => {
      const sentinel = sentinelRef.current;
      if (sentinel && !sentinel.released) {
        void sentinel.release();
      }
    };
  }, []);

  return { active, supported, request, release };
}
